import Link from 'next/link';

type ButtonProps = {
  buttonType: 'button';
  text: string;
  buttonStyle?: 'primary' | 'secondary';
  isLoading?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>;

type LinkProps = {
  buttonType: 'link';
  text: string;
  buttonStyle?: 'primary' | 'secondary';
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className'>;

function isButtonProps(props: ButtonProps | LinkProps): props is ButtonProps {
  return (props as ButtonProps).buttonType === 'button';
}

export const FormSubmitButtons: React.FC<{
  buttons: (ButtonProps | LinkProps)[];
}> = ({ buttons }) => (
  <div className="tw-grid tw-grid-cols-1 tw-justify-items-center tw-gap-[1rem]">
    {buttons.map((props, index) => (
      <div key={index} className="tw-w-full tw-max-w-[20rem]">
        {isButtonProps(props) ? (
          <Button {...props} />
        ) : (
          <LinkButton {...props} />
        )}
      </div>
    ))}
  </div>
);

const Button: React.FC<ButtonProps> = ({
  buttonType: _,
  text,
  isLoading,
  buttonStyle = 'primary',
  ...attr
}) => (
  <button
    type="button"
    className="tw-group tw-relative tw-block tw-w-full tw-appearance-none tw-rounded-4"
    {...attr}
  >
    <div
      className={
        buttonStyle === 'primary'
          ? 'tw-absolute tw-inset-0 tw-rounded-4 tw-bg-brand-lv1 tw-transition-[filter] group-hover:tw-brightness-110'
          : 'tw-absolute tw-inset-0 tw-rounded-4 tw-border-[2px] tw-border-brand-lv1 tw-bg-base-lv1 tw-transition-[filter] group-hover:tw-brightness-110'
      }
    />
    <div className="tw-relative tw-grid tw-min-h-[3rem] tw-grid-cols-1 tw-justify-items-center tw-px-[1rem] tw-py-[0.5rem]">
      <div className="tw-grid tw-w-fit tw-grid-cols-[1fr,auto,1fr] tw-items-center tw-justify-items-center tw-gap-[0.5rem]">
        <div></div>
        <div
          className={
            buttonStyle === 'primary'
              ? 'tw-text-button tw-text-base-lv1'
              : 'tw-text-button tw-text-brand-lv1'
          }
        >
          {text}
        </div>
        <div>
          {isLoading && (
            <div className="">
              <svg
                aria-hidden="true"
                className="tw-mx-auto tw-h-[0.875rem] tw-w-[0.875rem] tw-animate-spin tw-fill-base-lv1"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  </button>
);

const LinkButton: React.FC<LinkProps> = ({
  buttonType: _,
  text,
  buttonStyle = 'primary',
  ...attr
}) => (
  <Link
    href={attr.href ?? '#'}
    passHref
    className="tw-group tw-relative tw-block tw-w-full tw-appearance-none tw-rounded-4"
    {...attr}
  >
    <div
      className={
        buttonStyle === 'primary'
          ? 'tw-absolute tw-inset-0 tw-rounded-4 tw-bg-brand-lv1 tw-transition-[filter] group-hover:tw-brightness-110'
          : 'tw-absolute tw-inset-0 tw-rounded-4 tw-border-[2px] tw-border-brand-lv1 tw-bg-base-lv1 tw-transition-[filter] group-hover:tw-brightness-110'
      }
    />
    <div className="tw-relative tw-grid tw-min-h-[3rem] tw-grid-cols-1 tw-justify-items-center tw-px-[1rem] tw-py-[0.5rem]">
      <div className="tw-grid tw-w-fit tw-grid-cols-[1fr,auto,1fr] tw-items-center tw-justify-items-center tw-gap-[0.5rem]">
        <div></div>
        <div
          className={
            buttonStyle === 'primary'
              ? 'tw-text-button tw-text-base-lv1'
              : 'tw-text-button tw-text-brand-lv1'
          }
        >
          {text}
        </div>
        <div></div>
      </div>
    </div>
  </Link>
);
